import { Component } from '@angular/core';
import { ModalController, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import {
  CustomerRebate,
  WeeklyRebate,
  RebateTakeoutOptions,
  RebateTakeoutOptionsEnum,
} from '@usf/product-types';
import { CustomerRebatesService } from '@shared/services/customer-rebates/customer-rebates.service';
import { Subscription } from 'rxjs';
import { PlatformService, PlatformEnum } from '@panamax/app-state';
import { ActivatedRoute } from '@angular/router';
import { ChipTypeEnum } from '@shared/components/usf-chip/models/chip-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { FormatCurrencyAndDecimalPipe } from '@shared/pipes/format-currency-and-decimal.pipe';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { PATHS } from '@shared/constants/paths';
import Swiper from 'swiper';
import { ModalService } from '@shared/services/modal/modal.service';
import { TextModalTouchComponent } from '@shared/components/text-modal/text-modal-touch/text-modal-touch.component';
import { TextModalDesktopComponent } from '@shared/components/text-modal/text-modal-desktop/text-modal-desktop.component';
import { DatePipe } from '@angular/common';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { WeeklyRebateWithSpentMore } from './models/weekly-rebate-view-model';
@Component({
  selector: 'app-rebates',
  templateUrl: './rebates.page.html',
  providers: [FormatCurrencyAndDecimalPipe],
  styleUrls: ['./rebates.page.scss'],
})
export class RebatesPage implements ViewWillEnter, ViewWillLeave {
  TOTAL_WEEKS = 13;
  takeoutOption: RebateTakeoutOptionsEnum;
  rebateInfo?: CustomerRebate;
  private subscription?: Subscription;
  private takeoutOptionRouteKey = 'takeoutOption';
  partnerTakeoutOptions = RebateTakeoutOptions.PartnerWithUs;
  chipType = ChipTypeEnum.customerPill;
  maxSpend: number;
  currentSpend: number;
  minSpend: number;
  minSpendPercent: number;
  selectedIncentiveProgram: CustomerRebate;
  currentWeek: WeeklyRebate;
  sortedWeeklyRebates: WeeklyRebateWithSpentMore[];
  barProgress: number;
  cardTitleText: string;
  createOrderBoxText: string;
  createOrderBoxTitle: string;
  createOrderButtonText: string;
  goalNotReachedLabel: string;
  congratsSubtitle: string;
  rebateEndDate: Date;
  isRebateActive: boolean;
  wasRebateReached: boolean;
  isMobile = false;
  readonly PlatformEnum = PlatformEnum;
  get platformType(): PlatformEnum {
    return this.platformService.platformType;
  }

  constructor(
    private customerRebatesService: CustomerRebatesService,
    readonly platformService: PlatformService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private formatCurrencyAndDecimalPipe: FormatCurrencyAndDecimalPipe,
    private navigationHelperService: NavigationHelperService,
    private modalService: ModalService,
    private modalController: ModalController,
    private datePipe: DatePipe,
    private analyticsService: ProductAnalyticsService,
  ) {
    this.takeoutOption = this.route.snapshot.data[
      this.takeoutOptionRouteKey
    ] as RebateTakeoutOptionsEnum;
  }

  ionViewWillEnter() {
    this.analyticsService.trackRebatesPageLoad(this.takeoutOption);
    this.isMobile =
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.mobile;
    this.subscription = this.customerRebatesService
      .getIncentivePrograms()
      .subscribe(incentivePrograms => {
        this.selectedIncentiveProgram = incentivePrograms?.find(program =>
          Object.values(RebateTakeoutOptions).some(option =>
            option.includes(program.icmTakeoutOption),
          ),
        );
        this.currentWeek = this.selectedIncentiveProgram?.weeklyRebates?.reduce(
          (a, b) =>
            new Date(a.fiscalWeekStartDate) > new Date(b.fiscalWeekStartDate)
              ? a
              : b,
        );
        const effDate = new Date(this.selectedIncentiveProgram?.icmEffDate);
        this.rebateEndDate = new Date(effDate);
        this.rebateEndDate.setDate(effDate.getDate() + this.TOTAL_WEEKS * 7);
        this.currentSpend = this.currentWeek.totalSpend;
        this.isRebateActive = this.rebateEndDate >= new Date();

        switch (this.takeoutOption) {
          case RebateTakeoutOptionsEnum.PartnerWithUs:
            this.cardTitleText = this.translateService.instant(
              'i18n.rebatesPage.partnerWithUs',
            );
            this.minSpend = this.selectedIncentiveProgram?.tierSpendMin;
            this.maxSpend = this.selectedIncentiveProgram?.tierSpendMax;
            if (this.isMobile) {
              if (this.currentSpend <= this.minSpend) {
                this.barProgress =
                  (this.currentSpend / Math.max(this.minSpend, 1)) * 0.5;
              } else {
                const remainingProgress =
                  ((this.currentSpend - this.minSpend) /
                    Math.max(this.maxSpend - this.minSpend, 1)) *
                  0.5;
                this.barProgress = 0.5 + remainingProgress;
              }
              this.minSpendPercent = 50;
            } else {
              this.barProgress = this.currentSpend / this.maxSpend;
              this.minSpendPercent = (this.minSpend / this.maxSpend) * 100;
            }
            this.createOrderBoxTitle = this.translateService.instant(
              `i18n.rebatesPage.cardCreateOrderBannerTitle`,
              {
                maxEarn: this.formatCurrencyAndDecimalPipe.transform(
                  this.selectedIncentiveProgram?.maxEarn,
                  '1.0-0',
                ),
              },
            );
            const remainingSpend =
              this.maxSpend / this.TOTAL_WEEKS -
              this.currentWeek?.weeklyAvgSpend;
            this.createOrderBoxText = this.translateService.instant(
              'i18n.rebatesPage.cardCreateOrderBannerText',
              {
                remainingSpend: this.formatCurrencyAndDecimalPipe.transform(
                  remainingSpend > 0 ? remainingSpend : 0,
                  '1.0-0',
                ),
              },
            );
            this.createOrderButtonText = this.translateService.instant(
              'i18n.rebatesPage.cardCreateOrderBannerButton',
            );
            this.wasRebateReached =
              this.currentWeek?.totalSpend >=
              this.selectedIncentiveProgram?.tierSpendMin;
            this.goalNotReachedLabel = this.translateService.instant(
              'i18n.rebatesPage.cardEndedUnsuccessfulText',
            );
            this.congratsSubtitle = this.translateService.instant(
              'i18n.rebatesPage.congratsSubtitle',
            );
            break;
          case RebateTakeoutOptionsEnum.GetToKnowUs:
            this.cardTitleText = this.translateService.instant(
              'i18n.rebatesPage.getToKnowUs',
            );
            this.createOrderBoxTitle = this.translateService.instant(
              'i18n.rebatesPage.cardIncreaseWeeklySpend',
            );
            this.createOrderButtonText = this.translateService.instant(
              'i18n.rebatesPage.cardCreateOrder',
            );
            this.wasRebateReached =
              this.selectedIncentiveProgram?.toDateAgrTotal > 0;
            this.goalNotReachedLabel = this.translateService.instant(
              'i18n.rebatesPage.cardEndedUnsuccessfulText2',
            );
            this.congratsSubtitle = this.translateService.instant(
              'i18n.rebatesPage.congratsSubtitle2',
            );
            this.sortedWeeklyRebates =
              this.selectedIncentiveProgram?.weeklyRebates
                ?.slice()
                .sort((a: WeeklyRebate, b: WeeklyRebate) =>
                  new Date(a.fiscalWeekStartDate) <
                  new Date(b.fiscalWeekStartDate)
                    ? -1
                    : 1,
                );
            this.sortedWeeklyRebates = this.sortedWeeklyRebates.map(
              (rebate, index) => {
                const previousRebate =
                  index > 0 ? this.sortedWeeklyRebates[index - 1] : null;
                return {
                  ...rebate,
                  spentMore: previousRebate
                    ? rebate.weekSpend > previousRebate.weekSpend
                    : false,
                };
              },
            );
            break;
        }
      });
  }

  ionViewWillLeave() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  getMoreDeals() {
    this.navigationHelperService.routeTo(PATHS.DEALS);
  }

  createOrder() {
    this.navigationHelperService.routeTo(PATHS.LISTS);
  }

  async showAgreementDetails() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      this.platformService.isTouch.value
        ? TextModalTouchComponent
        : TextModalDesktopComponent,
      {
        title: this.translateService.instant(
          'i18n.rebatesPage.agreementDetails',
        ),
        subtitle: `${this.translateService.instant(
          'i18n.rebatesPage.cardValidThrough',
        )} <b>${this.datePipe.transform(this.rebateEndDate, 'MMMM d,yyyy')}</b>`,
        text: this.translateService.instant(
          'i18n.rebatesPage.agreementDetailsModalText',
        ),
      },
      'modal-desktop-size-xl',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  async showIncentiveFAQs() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      this.platformService.isTouch.value
        ? TextModalTouchComponent
        : TextModalDesktopComponent,
      {
        title: this.translateService.instant('i18n.rebatesPage.incentiveFAQs'),
        subtitle: `${this.translateService.instant(
          'i18n.rebatesPage.cardValidThrough',
        )} <b>${this.datePipe.transform(this.rebateEndDate, 'MMMM d,yyyy')}</b>`,
        text: this.translateService.instant(
          'i18n.rebatesPage.incentiveFAQsModalText',
        ),
      },
      'modal-desktop-size-xl',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  setInitialWeek(swiper: Swiper) {
    swiper.slideTo(this.selectedIncentiveProgram?.weeklyRebates?.length - 1);
  }

  trackByIndex = (index: number) => index;
}
